/**
 * Per-level data the views share: the compiled World (for visible-object queries), the
 * camera guide and the rendering cache. Built lazily, once per level.
 */
import { type LevelData, World } from '@cube-rush/shared';
import { LevelArt } from '../render/levelArt';
import { seasonTheme } from '../render/seasonTheme';
import { themeFor } from '../render/themes';
import type { Season } from '../season/season';
import { LevelGuide } from './camera';

export interface LevelBundle {
  index: number;
  level: LevelData;
  world: World;
  guide: LevelGuide;
  art: LevelArt;
}

export class LevelResources {
  private readonly bundles = new Map<number, LevelBundle>();
  /** The holiday look of the levels (their art is drawn in the season's version of the theme). */
  private season: Season = 'none';

  constructor(private readonly levels: readonly LevelData[]) {}

  get count(): number {
    return this.levels.length;
  }

  /** Level `index` (clamped to the race). */
  get(index: number): LevelBundle {
    const last = this.levels.length - 1;
    const i = Number.isInteger(index) ? Math.min(Math.max(index, 0), last) : 0;
    let bundle = this.bundles.get(i);
    if (!bundle) {
      const level = this.levels[i]!;
      const world = new World(level);
      bundle = {
        index: i,
        level,
        world,
        guide: new LevelGuide(level),
        art: this.artOf(level, world),
      };
      this.bundles.set(i, bundle);
    }
    return bundle;
  }

  /**
   * Switches the holiday look (the decorations setting changed mid-race): the levels keep
   * their bundles (views compare them to notice a new level), only the art is new.
   */
  setSeason(season: Season): void {
    if (season === this.season) return;
    this.season = season;
    for (const bundle of this.bundles.values()) bundle.art = this.artOf(bundle.level, bundle.world);
  }

  private artOf(level: LevelData, world: World): LevelArt {
    return new LevelArt(level, world, seasonTheme(themeFor(level.theme), this.season));
  }
}
